import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import '../styles/Modal.css';
import '../styles/OrgBrandModal.css';

const OrgBrandModal = ({ isOpen, onClose, onConfirm, onManageOrganizations = null, user, isProxyMode = false, currentOrganization = null, currentBrand = null }) => {
  const [organizations, setOrganizations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
      fetchBrands();
      // Initialize with current selections
      setSelectedOrg(currentOrganization);
      setSelectedBrand(currentBrand);
    }
  }, [isOpen, currentOrganization, currentBrand]);

  // Filter brands based on selected organization
  const filteredBrands = selectedOrg
    ? brands.filter(b => b.organization_id === (selectedOrg._id || selectedOrg.id))
    : [];

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user/organizations', {
        withCredentials: true
      });
      const orgs = response.data.organizations || [];
      // Sort organizations alpha-numerically
      const sortedOrgs = orgs.sort((a, b) => {
        const nameA = (a.organization_name || '').toLowerCase();
        const nameB = (b.organization_name || '').toLowerCase();
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
      setOrganizations(sortedOrgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/api/brands/list', {
        withCredentials: true
      });
      const allBrands = response.data.brands || [];

      // Sort brands alpha-numerically
      const sortedBrands = allBrands.sort((a, b) => {
        const nameA = (a.brand_name || a.name || '').toLowerCase();
        const nameB = (b.brand_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });

      setBrands(sortedBrands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    // Auto-select the first brand for this org instead of leaving it blank
    const orgBrands = brands.filter(b => b.organization_id === (org._id || org.id));
    setSelectedBrand(orgBrands.length > 0 ? orgBrands[0] : null);
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
  };

  const handleConfirm = () => {
    if (selectedOrg && selectedBrand) {
      onConfirm(selectedOrg, selectedBrand);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-large org-brand-modal">
        <div className="modal-header">
          <h3 className="modal-title">Select Organization and Brand</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close" />
        </div>

        <div className="modal-body org-brand-modal-body">
          <div className="org-brand-columns">
            <div className="org-brand-column">
              <h4>Organization:</h4>
              <div className="org-brand-list">
                {loading ? (
                  <div className="org-brand-loading">Loading...</div>
                ) : organizations.length === 0 ? (
                  <div className="org-brand-empty">No organizations available</div>
                ) : (
                  organizations.map((org) => (
                    <div
                      key={org._id || org.id}
                      className={`org-brand-item ${selectedOrg?._id === org._id || selectedOrg?.id === org.id ? 'selected' : ''}`}
                      onClick={() => handleOrgSelect(org)}
                    >
                      {org.icon_url ? (
                        <BrandIcon src={org.icon_url} alt={org.organization_name} className="org-brand-item-icon" />
                      ) : (
                        <div className="org-brand-item-placeholder">
                          <Icon name="building" size="1rem" />
                        </div>
                      )}
                      <span className="org-brand-item-name">{org.organization_name}</span>
                      {onManageOrganizations && (
                        <button
                          type="button"
                          className="org-brand-item-gear"
                          aria-label="Manage organizations"
                          title="Manage organizations"
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageOrganizations();
                          }}
                        >
                          <Icon name="gear" size="1rem" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="org-brand-divider"></div>

            <div className="org-brand-column">
              <h4>Brand:</h4>
              <div className="org-brand-list">
                {!selectedOrg ? (
                  <div className="org-brand-empty">Select an organization first</div>
                ) : filteredBrands.length === 0 ? (
                  <div className="org-brand-empty">No brands available</div>
                ) : (
                  filteredBrands.map((brand) => (
                    <div
                      key={brand._id || brand.id}
                      className={`org-brand-item ${selectedBrand?._id === brand._id || selectedBrand?.id === brand.id ? 'selected' : ''}`}
                      onClick={() => handleBrandSelect(brand)}
                    >
                      {brand.icon_url ? (
                        <BrandIcon src={brand.icon_url} alt={brand.brand_name || brand.name} className="org-brand-item-icon" />
                      ) : (
                        <div className="org-brand-item-placeholder">
                          <Icon name="tag" size="1rem" />
                        </div>
                      )}
                      <span className="org-brand-item-name">{brand.brand_name || brand.name}</span>
                      {onManageOrganizations && (
                        <button
                          type="button"
                          className="org-brand-item-gear"
                          aria-label="Manage organizations"
                          title="Manage organizations"
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageOrganizations();
                          }}
                        >
                          <Icon name="gear" size="1rem" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer org-brand-modal-footer">
          <button
            className="modal-btn modal-btn-confirm"
            onClick={handleConfirm}
            disabled={!selectedOrg || !selectedBrand}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgBrandModal;
